import { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { GraduationCap, CheckCircle, Play } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const CAT_COLORS = { onboarding: 'bg-blue-100 text-blue-700', catalog: 'bg-purple-100 text-purple-700', orders: 'bg-orange-100 text-orange-700', compliance: 'bg-red-100 text-red-700', finance: 'bg-green-100 text-green-700', returns: 'bg-yellow-100 text-yellow-700', shipping: 'bg-teal-100 text-teal-700', tools: 'bg-pink-100 text-pink-700' };

export default function TrainingPage() {
  const [modules, setModules] = useState([]);
  const [stats, setStats] = useState({ total: 0, completed: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchModules(); }, []);

  const fetchModules = async () => {
    try {
      const { data } = await axios.get(`${API}/api/training/modules`, { withCredentials: true });
      setModules(data.modules || []);
      setStats({ total: data.total || 0, completed: data.completed || 0 });
    } catch { toast.error('Failed to load training'); }
    finally { setLoading(false); }
  };

  const markComplete = async (mid) => {
    try {
      await axios.post(`${API}/api/training/modules/${mid}/complete`, {}, { withCredentials: true });
      toast.success('Module completed!');
      fetchModules();
    } catch { toast.error('Failed to mark complete'); }
  };

  const pct = stats.total ? Math.round((stats.completed / stats.total) * 100) : 0;

  return (
    <DashboardLayout>
      <div className="p-6 max-w-4xl" data-testid="training-page">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#0A0A0A]">Training Center</h1>
          <p className="text-sm text-[#6b6b8a] mt-1">Learn how to use SellerPanel effectively</p>
        </div>

        <div className="bg-white rounded-xl border border-[#e5e5ea] p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-[#0A0A0A]">Your Progress</p>
            <span className="text-sm font-mono text-[#7d64ff]">{stats.completed}/{stats.total}</span>
          </div>
          <div className="w-full h-3 bg-[#f0f0f0] rounded-full overflow-hidden">
            <div className="h-full bg-[#7d64ff] rounded-full transition-all duration-500" style={{ width: `${pct}%` }} data-testid="training-progress" />
          </div>
          <p className="text-xs text-[#9c9db7] mt-2">{pct}% complete</p>
        </div>

        {loading ? <div className="text-center py-12 text-[#9c9db7]">Loading...</div> : (
          <div className="grid gap-3">
            {modules.map((m, i) => (
              <div key={m.module_id} className={`flex items-center gap-4 bg-white rounded-xl border p-4 ${m.completed ? 'border-green-200' : 'border-[#e5e5ea]'}`} data-testid={`training-module-${m.module_id}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${m.completed ? 'bg-green-100 text-green-700' : 'bg-[#7d64ff]/10 text-[#7d64ff]'}`}>
                  {m.completed ? <CheckCircle size={20} /> : i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`font-medium ${m.completed ? 'text-green-700 line-through' : 'text-[#0A0A0A]'}`}>{m.title}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${CAT_COLORS[m.category] || 'bg-gray-100 text-gray-600'}`}>{m.category}</span>
                    <span className="text-[10px] text-[#9c9db7]">{m.type} &middot; {m.duration_min} min</span>
                  </div>
                </div>
                {!m.completed && (
                  <Button size="sm" onClick={() => markComplete(m.module_id)} className="bg-[#7d64ff] hover:bg-[#6b4fef] text-xs" data-testid={`complete-module-${m.module_id}`}>
                    <Play size={12} className="mr-1" /> Complete
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
